import styled, { css } from 'styled-components';
import { TextBase } from '@/app/components/hotel-detail/common/text';
import Tooltip from '@/app/components/hotel-detail/Tooltip';
import { Text, type TextVariant } from '@lmn/design-system';
import { Icon } from '@/app/common/icons/Icon';
import { useTranslation } from '../../translations/Translation';

const doubleBedTypes: StaticRoomBedType[] = ['DOUBLE', 'QUEEN', 'KING'];

const BedOptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export function RoomBedOptions(
  props: {
    bedOptions: StaticRoomBedOption[];
    iconSize?: number;
    fontSize?: TextVariant;
  } & React.HTMLAttributes<HTMLElement>
) {
  const { bedOptions, iconSize = 16, fontSize = 'BodySmall', ...rest } = props;
  const { t } = useTranslation();

  return bedOptions.length ? (
    <div
      css={`
        display: flex;
        flex-direction: column;
        gap: 8px;
      `}
      {...rest}
    >
      {bedOptions.length > 1 ? (
        <BedOptionContainer>
          <Icon
            category="Services"
            name="icon-solid-undefined-bed"
            size={iconSize}
            color="iconPrimary"
            alt="Bed Type"
          />
          <Text variant={fontSize}>{t('vacation.select.room_panel.bedding.various_types')}</Text>
          <Tooltip
            css={`
              display: flex;
            `}
            label={<BedsTooltip bedOptions={bedOptions} />}
            placement="top"
            maxWidth={224}
          >
            <Icon
              category="Semantics"
              name="icon-circle-info"
              size={16}
              color="iconSecondary"
              alt="Beds information"
              css={`
                cursor: pointer;
              `}
            />
          </Tooltip>
        </BedOptionContainer>
      ) : (
        bedOptions.map((bedOption) => (
          <BedOptionContainer key={bedOption.code}>
            <Icon
              category="Services"
              name={
                bedOption.beds.length === 1 && doubleBedTypes.includes(bedOption.beds[0].type)
                  ? 'icon-solid-double-bed'
                  : 'icon-solid-single-bed'
              }
              size={iconSize}
              color="iconPrimary"
              alt="Bed Type"
            />
            <Text variant={fontSize}>
              {bedOption.beds
                .map((bed) => `${bed.quantity} ${bed.name}`)
                .join(` ${t('vacation.select.room_panel.bedding.and')} `)}
            </Text>
          </BedOptionContainer>
        ))
      )}
    </div>
  ) : undefined;
}

function BedsTooltip(props: { bedOptions: StaticRoomBedOption[] }) {
  const { bedOptions } = props;
  const { t } = useTranslation();
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        padding: 4px;
        gap: 8px;
        color: ${(p) => p.theme.kallisto.color.background.static.backgroundLayer01};
      `}
    >
      <Text variant="Title4" weight="bold" color="textOnDark">
        {t('vacation.select.room_panel.bedding.tooltip.title')}
      </Text>
      <ul
        css={css`
          margin: 0;
          padding-left: 20px;
          list-style-type: disc;
        `}
      >
        {bedOptions.map((bedOption) => (
          <li key={bedOption.code}>
            <TextBase color="textOnDark">
              {bedOption.beds
                .map((bed) => `${bed.quantity} ${bed.name}`)
                .join(` ${t('vacation.select.room_panel.bedding.and')} `)}
            </TextBase>
          </li>
        ))}
      </ul>
      <TextBase color="textOnDark">{t('vacation.select.room_panel.bedding.tooltip.description')}</TextBase>
    </div>
  );
}
